import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { Tooltip } from "react-aria-components";
import { cn } from "../lib/utils";
import { TooltipContent, TooltipProvider, TooltipTrigger } from "./tooltip";

const buttonVariants = cva(
	"inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
	{
		variants: {
			variant: {
				default: "bg-primary text-primary-foreground hover:bg-primary/90",
				destructive:
					"bg-destructive text-destructive-foreground hover:bg-destructive/90",
				outline:
					"border border-input bg-background hover:bg-accent hover:text-accent-foreground",
				secondary:
					"bg-secondary text-secondary-foreground hover:bg-secondary/80",
				ghost: "hover:bg-accent hover:text-accent-foreground",
				link: "text-primary underline-offset-4 hover:underline",
				expandIcon:
					"group relative text-primary-foreground bg-primary hover:bg-primary/90",
				shine:
					"text-primary-foreground animate-shine bg-linear-to-r from-primary via-primary/75 to-primary bg-[length:400%_100%] ",
				gooeyRight:
					"text-primary-foreground relative bg-primary z-0 overflow-hidden transition-all duration-500 before:absolute before:inset-0 before:-z-10 before:translate-x-[150%] before:translate-y-[150%] before:scale-[2.5] before:rounded-[100%] before:bg-linear-to-r from-zinc-400 before:transition-transform before:duration-1000  hover:before:translate-x-[0%] hover:before:translate-y-[0%] ",
				gooeyLeft:
					"text-primary-foreground relative bg-primary z-0 overflow-hidden transition-all duration-500 after:absolute after:inset-0 after:-z-10 after:translate-x-[-150%] after:translate-y-[150%] after:scale-[2.5] after:rounded-[100%] after:bg-linear-to-l from-zinc-400 after:transition-transform after:duration-1000  hover:after:translate-x-[0%] hover:after:translate-y-[0%] ",
				linkHover1:
					"relative after:absolute after:bg-primary after:bottom-2 after:h-[1px] after:w-2/3 after:origin-bottom-left after:scale-x-100 hover:after:origin-bottom-right hover:after:scale-x-0 after:transition-transform after:ease-in-out after:duration-300",
				linkHover2:
					"relative after:absolute after:bg-primary after:bottom-2 after:h-[1px] after:w-2/3 after:origin-bottom-right after:scale-x-0 hover:after:origin-bottom-left hover:after:scale-x-100 after:transition-transform after:ease-in-out after:duration-300",
				ringHover:
					"bg-primary text-primary-foreground transition-all duration-300 hover:bg-primary/90 hover:ring-2 hover:ring-primary/80 hover:ring-offset-2 hover:ring-offset-background",
				glowingRing:
					"bg-primary text-primary-foreground relative overflow-hidden ring-2 ring-primary/50 animate-glow transition-all",
				shimmer:
					"bg-linear-to-r from-primary via-primary/50 to-primary bg-[length:200%_100%] text-primary-foreground animate-shimmer transition-all",
				bouncing:
					"bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 transition-all animate-bounce",
			},
			size: {
				default: "h-9 px-4 py-2",
				sm: "h-8 rounded-md px-3 text-xs",
				lg: "h-10 rounded-md px-8",
				icon: "h-9 w-9",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	},
);

interface IconProps {
	Icon: React.ElementType;
	iconPlacement: "left" | "right";
}

interface IconRefProps {
	Icon?: never;
	iconPlacement?: undefined;
}

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean;
	loading?: boolean;
	loadingText?: string;
	tooltipText?: string;
	ref?: React.RefObject<HTMLButtonElement>;
}

export type ButtonIconProps = IconProps | IconRefProps;

const Button = ({
	ref,
	className,
	variant,
	size,
	asChild = false,
	Icon,
	iconPlacement,
	loading,
	loadingText = "Loading...",
	tooltipText,
	children,
	...props
}: ButtonProps & ButtonIconProps) => {
	const Comp = asChild ? Slot : "button";
	const content = loading ? (
		<>
			{size === "icon" ? (
				<Loader2 className={cn("animate-spin")} />
			) : (
				<>
					{loadingText}
					<Loader2 className={cn("ml-2 h-4 w-4 animate-spin")} />
				</>
			)}
		</>
	) : (
		children
	);

	return tooltipText ? (
		<TooltipProvider>
			<Tooltip>
				<TooltipTrigger>
					<Comp
						className={cn(buttonVariants({ variant, size, className }))}
						ref={ref}
						disabled={loading}
						{...props}
					>
						{content}
					</Comp>
				</TooltipTrigger>
				<TooltipContent>{tooltipText}</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	) : (
		<Comp
			className={cn(buttonVariants({ variant, size, className }))}
			ref={ref}
			disabled={loading}
			{...props}
		>
			{content}
		</Comp>
	);
};
Button.displayName = "Button";

export { Button, buttonVariants };
